<!--
  SuperAdmin: Users -- platform-wide user administration.

  Card: SA Users page + per-user detail -- https://trello.com/c/tRC6K0Zn
  Backend: api/src/endpoints/superadmin_users.py

  This is the cross-tenant view no other page has: EVERY account across
  EVERY tenant. It REPLACES the legacy /admin/accounts list
  (components/Account/AccountList.vue), whose columns are all carried
  forward here so the replacement loses no capability:

    legacy AccountList     -> this page
    ------------------------------------------------------
    ID                     -> (row testid; not a column)
    Username               -> Username
    Email                  -> Email
    Distributor            -> Distributor
    Store                  -> Client / Store
    Role                   -> Role
    Active                 -> Active
    (none)                 -> Name          (added by the spec)
    (none)                 -> Last login    (added by the spec)

  NOT the distributor Team & Permissions page -- that stays as
  distributor self-service over their own roster.

  Access: route-gated on superadmin.access. Writes live on the detail
  and create pages and are gated on superadmin.settings server-side.

  Sits beside the capability x role matrix under the SA sidebar's
  Access Control group.

  ASCII-only.
-->
<template>
    <div data-testid="sa-users">
        <div class="sa-users__header">
            <h1 class="sa-page-title">Users</h1>
            <button
                type="button"
                class="sa-btn sa-btn--primary"
                data-testid="sa-users-create"
                @click="goCreate"
            >
                New user
            </button>
        </div>

        <p class="sa-users__subtitle">
            Every account across every tenant.
        </p>

        <!-- Search + filters -->
        <div class="sa-users__filters">
            <input
                type="search"
                class="sa-input"
                placeholder="Search name, username, or email"
                data-testid="sa-users-search"
                :value="search"
                @input="onSearchInput"
            />

            <select
                class="sa-input"
                data-testid="sa-users-filter-distributor"
                v-model="filters.distributor_id"
                @change="reload"
            >
                <option value="">All distributors</option>
                <option
                    v-for="d in options.distributors"
                    :key="d.id"
                    :value="String(d.id)"
                >
                    {{ d.name }}
                </option>
            </select>

            <select
                class="sa-input"
                data-testid="sa-users-filter-client"
                v-model="filters.client_id"
                @change="reload"
            >
                <option value="">All clients</option>
                <option
                    v-for="c in options.clients"
                    :key="c.id"
                    :value="String(c.id)"
                >
                    {{ c.name }}
                </option>
            </select>

            <select
                class="sa-input"
                data-testid="sa-users-filter-role"
                v-model="filters.role_id"
                @change="reload"
            >
                <option value="">All roles</option>
                <option
                    v-for="r in options.roles"
                    :key="r.id"
                    :value="String(r.id)"
                >
                    {{ r.name }}
                </option>
            </select>

            <select
                class="sa-input"
                data-testid="sa-users-filter-active"
                v-model="filters.active"
                @change="reload"
            >
                <option value="">Active and inactive</option>
                <option value="true">Active only</option>
                <option value="false">Inactive only</option>
            </select>
        </div>

        <p v-if="loading" class="sa-state" data-testid="sa-users-loading">
            Loading...
        </p>

        <p
            v-else-if="errorMsg"
            class="sa-state sa-state--error"
            data-testid="sa-users-error"
        >
            {{ errorMsg }}
        </p>

        <template v-else>
            <p
                v-if="rows.length === 0"
                class="sa-state"
                data-testid="sa-users-empty"
            >
                No users match these filters.
            </p>

            <div v-else class="sa-table-wrap">
                <table class="sa-table" data-testid="sa-users-table">
                    <thead>
                        <tr>
                            <th data-testid="sa-users-col-name">Name</th>
                            <th data-testid="sa-users-col-username">
                                Username
                            </th>
                            <th data-testid="sa-users-col-email">Email</th>
                            <th data-testid="sa-users-col-role_name">Role</th>
                            <th data-testid="sa-users-col-distributor_name">
                                Distributor
                            </th>
                            <th data-testid="sa-users-col-client_name">
                                Client / Store
                            </th>
                            <th data-testid="sa-users-col-active">Active</th>
                            <th data-testid="sa-users-col-last_login_at">
                                Last login
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in rows"
                            :key="row.id"
                            class="sa-table__row"
                            :data-testid="`sa-users-row-${row.id}`"
                            @click="openUser(row.id)"
                        >
                            <td>{{ fullName(row) }}</td>
                            <td>{{ row.username }}</td>
                            <td>{{ row.email || "--" }}</td>
                            <td>{{ row.role_name || "--" }}</td>
                            <td>
                                <DistributorLink
                                    :distributor-id="row.distributor_id"
                                    :name="row.distributor_name"
                                    fallback="--"
                                />
                            </td>
                            <td>{{ scopeLabel(row) }}</td>
                            <td :data-testid="`sa-users-active-${row.id}`">
                                {{ row.active ? "Yes" : "No" }}
                            </td>
                            <td>{{ formatDate(row.last_login_at) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p class="sa-users__count">
                Showing {{ rows.length }} of {{ total }}
            </p>
        </template>
    </div>
</template>

<script>
import DistributorLink from "@/components/superadmin/DistributorLink.vue";
import {
    listUsers,
    getFilterOptions,
} from "@/services/superadminUsersService";

// Search is debounced so typing does not fire a request per keystroke.
const SEARCH_DEBOUNCE_MS = 300;

export default {
    components: { DistributorLink },
    name: "SuperAdminUsersList",
    data() {
        return {
            loading: true,
            errorMsg: "",
            rows: [],
            total: 0,
            search: "",
            filters: {
                distributor_id: "",
                client_id: "",
                role_id: "",
                active: "",
            },
            options: { distributors: [], clients: [], roles: [] },
            searchTimer: null,
        };
    },
    async mounted() {
        // Filter options are best-effort: a failure there must not stop
        // the list itself from rendering.
        getFilterOptions()
            .then((resp) => {
                const d = resp && resp.data ? resp.data : resp;
                this.options = {
                    distributors: (d && d.distributors) || [],
                    clients: (d && d.clients) || [],
                    roles: (d && d.roles) || [],
                };
            })
            .catch(() => {
                this.options = { distributors: [], clients: [], roles: [] };
            });

        await this.reload();
    },
    beforeUnmount() {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
    },
    methods: {
        fullName(row) {
            const name = [row.first_name, row.last_name]
                .filter(Boolean)
                .join(" ")
                .trim();
            return name || "--";
        },

        // Client and store share a column: a user is scoped to a client,
        // to store(s) via account_stores, or to neither.
        scopeLabel(row) {
            const parts = [];
            if (row.client_name) {
                parts.push(row.client_name);
            }
            if (row.store_names && row.store_names.length) {
                parts.push(row.store_names.join(", "));
            }
            return parts.length ? parts.join(" / ") : "--";
        },

        formatDate(value) {
            if (!value) {
                return "Never";
            }
            // Trim the time-of-day; the list only needs the day.
            return String(value).slice(0, 10);
        },

        onSearchInput(event) {
            this.search = event.target.value;
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }
            this.searchTimer = setTimeout(() => {
                this.reload();
            }, SEARCH_DEBOUNCE_MS);
        },

        queryParams() {
            return {
                q: this.search,
                distributor_id: this.filters.distributor_id,
                client_id: this.filters.client_id,
                role_id: this.filters.role_id,
                active: this.filters.active,
                per_page: 100,
            };
        },

        async reload() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const resp = await listUsers(this.queryParams());
                const data = resp && resp.data ? resp.data : resp;
                this.rows = (data && data.rows) || [];
                this.total = (data && data.total) || 0;
            } catch (e) {
                this.errorMsg =
                    "Could not load users. Please try again.";
                this.rows = [];
                this.total = 0;
            } finally {
                this.loading = false;
            }
        },

        openUser(id) {
            this.$router.push(`/admin/users/${id}`);
        },

        goCreate() {
            this.$router.push("/admin/users/new");
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0;
    color: #1a202c;
}
.sa-users__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}
.sa-users__subtitle {
    font-size: 13px;
    color: var(--tf-fg-3);
    margin: 4px 0 16px;
}
.sa-users__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
}
.sa-input {
    font-size: 13px;
    padding: 6px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    background: #fff;
    color: #2d3748;
    min-width: 160px;
}
.sa-btn {
    font-size: 13px;
    font-weight: 500;
    padding: 7px 14px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #2d3748;
    cursor: pointer;
}
.sa-btn--primary {
    background: #2b6cb0;
    border-color: #2b6cb0;
    color: #fff;
}
.sa-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-state--error {
    color: #c53030;
}
.sa-table-wrap {
    overflow-x: auto;
}
.sa-table {
    border-collapse: collapse;
    font-size: 13px;
    width: 100%;
}
.sa-table th,
.sa-table td {
    border: 1px solid #e2e8f0;
    padding: 8px 10px;
    text-align: left;
    vertical-align: middle;
}
.sa-table th {
    background: #f7fafc;
    font-weight: 600;
    color: #2d3748;
    white-space: nowrap;
}
.sa-table__row {
    cursor: pointer;
}
.sa-table__row:hover {
    background: #f7fafc;
}
.sa-users__count {
    font-size: 12px;
    color: var(--tf-fg-4);
    margin-top: 10px;
}
</style>
